import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { pickLocalized } from "@/i18n/translations";
import api from "@/lib/api";
import BookingClosedBanner, { useBookingConfig } from "@/components/BookingClosedBanner";
import SumUpBadge from "@/components/payments/SumUpBadge";

export default function EventDetail() {
  const { id } = useParams();
  const { t, locale } = useI18n();
  const config = useBookingConfig();
  const bookingOpen = !config || config.booking_open;
  const [event, setEvent] = useState(null);
  const [qty, setQty] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => setError("not_found"));
  }, [id]);

  if (error === "not_found") {
    return (
      <div className="container-prieure py-32 text-center">
        <p className="font-display text-3xl">404</p>
      </div>
    );
  }
  if (!event) {
    return (
      <div className="container-prieure py-32 text-center font-body" style={{ color: "var(--muted)" }}>
        {t.common.loading}
      </div>
    );
  }

  const total = (event.price_eur || 0) * qty;

  const handlePay = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/checkout/event", {
        event_id: event.id,
        quantity: qty,
        name,
        email,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur");
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="event-detail">
      <section className="relative" style={{ height: "50vh", minHeight: 380 }}>
        <img src={event.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-12">
          <Link to="/evenements" className="font-caps text-[0.7rem] text-white/80 mb-4">
            ← {t.common.back}
          </Link>
          <div className="font-caps text-[0.72rem]" style={{ color: "#D4AF37" }}>
            {new Date(event.date).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", {
              weekday: "long", day: "numeric", month: "long", year: "numeric"
            })}
            {event.end_date && event.end_date !== event.date && (
              <> {locale === "fr" ? "→" : "→"} {new Date(event.end_date).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", {
                weekday: "long", day: "numeric", month: "long", year: "numeric"
              })}</>
            )}
            {event.time ? ` · ${event.time}${event.end_time ? `–${event.end_time}` : ""}` : ""}
            {event.recurrence && event.recurrence !== "none" && (
              <span className="ml-3 px-2 py-0.5 border" style={{ borderColor: "#D4AF37", letterSpacing: "0.15em", fontSize: "0.6rem" }}>
                {event.recurrence === "daily"
                  ? (locale === "fr" ? "QUOTIDIEN" : "DAILY")
                  : (locale === "fr" ? "HEBDOMADAIRE" : "WEEKLY")}
                {event.recurrence_until && ` · ${locale === "fr" ? "JUSQU'AU" : "UNTIL"} ${new Date(event.recurrence_until).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
              </span>
            )}
          </div>
          <h1 className="font-display text-5xl sm:text-6xl text-white mt-2">
            {pickLocalized(event.title, locale)}
          </h1>
          <p className="font-body text-white/80 mt-2">📍 {pickLocalized(event.location, locale)}</p>
        </div>
      </section>

      <section className="py-20">
        <div className={`container-prieure grid grid-cols-1 ${event.ticketed ? "md:grid-cols-12" : ""} gap-12`}>
          <div className={event.ticketed ? "md:col-span-7" : ""}>
            <p className="font-body text-lg leading-relaxed drop-cap" style={{ color: "var(--ink-2)" }}>
              {pickLocalized(event.description, locale)}
            </p>
          </div>
          {event.ticketed && (
          <div className="md:col-span-5">
            <div className="illuminated-border" style={{ background: "white" }}>
              <form onSubmit={handlePay} data-testid="ticket-form">
                  <div className="label-caps mb-2" style={{ color: "var(--cinnabar)" }}>
                    {t.eventDetail.bookTitle}
                  </div>
                  <h3 className="font-display text-3xl mb-6">{event.price_eur}{t.common.euro}</h3>
                  <BookingClosedBanner />

                  <label className="label-caps">{t.eventDetail.quantity}</label>
                  <input
                    data-testid="ticket-qty"
                    type="number" min="1" max="20" value={qty}
                    onChange={(e) => setQty(parseInt(e.target.value || "1", 10))}
                    className="input-classic mt-1 mb-4"
                  />

                  <label className="label-caps">{t.eventDetail.fullName}</label>
                  <input
                    data-testid="ticket-name"
                    required value={name} onChange={(e) => setName(e.target.value)}
                    className="input-classic mt-1 mb-4"
                  />

                  <label className="label-caps">{t.eventDetail.email}</label>
                  <input
                    data-testid="ticket-email"
                    required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="input-classic mt-1 mb-6"
                  />

                  <div className="flex items-center justify-between mb-6">
                    <span className="label-caps">{t.eventDetail.total}</span>
                    <span className="font-display text-2xl">{total.toFixed(2)} €</span>
                  </div>

                  {error && <p className="font-body text-sm mb-3" style={{ color: "var(--cinnabar)" }}>{String(error)}</p>}

                  <button data-testid="ticket-submit" type="submit" disabled={submitting || !bookingOpen}
                          className="btn btn-primary w-full">
                    {!bookingOpen ? (locale === "fr" ? "Bientôt disponible" : "Coming soon") : (submitting ? t.common.loading : t.eventDetail.payNow)}
                  </button>
                  <div className="flex justify-center mt-4">
                    <SumUpBadge />
                  </div>
                </form>
            </div>
          </div>
          )}
        </div>
      </section>
    </div>
  );
}
